import { useCallback, useRef } from 'react';

/**
 * Keeps the toast stack clear of a page's sticky bottom action bar (KBar):
 * attach the returned ref to the bar and its rendered height is published on
 * the body as --toast-clearance, which ToastViewport adds to its bottom
 * offset. All lifecycle lives in the callback ref (React calls it with null
 * on unmount) — an effect-based cleanup would be re-run by StrictMode's
 * simulated remount without the ref ever re-attaching, killing the observer.
 */
export const useToastClearance = () => {
	const observerRef = useRef<ResizeObserver | null>(null);

	return useCallback((node: HTMLElement | null) => {
		observerRef.current?.disconnect();
		observerRef.current = null;

		if (!node) {
			document.body.style.removeProperty('--toast-clearance');
			return;
		}

		const observer = new ResizeObserver(() => {
			document.body.style.setProperty(
				'--toast-clearance',
				`${node.offsetHeight}px`
			);
		});
		observer.observe(node);
		observerRef.current = observer;
	}, []);
};
